import * as React from 'react';
import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';

import { PermissionModel } from '@app/Models/Models';

import ReactSelect from '@app/Components/ReactSelect/ReactSelect';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { permissionStore } from '@app/Stores/PermissionStore';

type PermissionsPickerProps = {
    value?: string;
    onSelect?: (value: string | null) => void;
    loader?: PromiseCompletion;
    className?: string;
    placeholder?: string;
    enableStrDecoding?: boolean;
    filterOptions?: (options: PermissionModel[]) => PermissionModel[];
};

@observer
export default class PermissionsPicker extends React.Component<PermissionsPickerProps> {
    @observable private _selectedIds: string = '';
    @observable private _inputValue: string = '';

    constructor(props: PermissionsPickerProps) {
        super(props);
        this._selectedIds = props.value || '';
    }

    componentDidMount() {
        if (!permissionStore.permissions.length) {
            const promise = permissionStore.loadPermissions();
            this.props.loader?.subscribe(promise);
        }
    }

    componentDidUpdate(prevProps: PermissionsPickerProps) {
        if (prevProps.value !== this.props.value) {
            this._selectedIds = this.props.value || '';
        }
    }

    render() {
        const { className, placeholder } = this.props;
        return (
            <ReactSelect
                options={this._permissionOptions}
                value={this._selectedIds}
                onSelect={this._onSelectPermission}
                isOptionDisabled={(option) => option.value === '-1'}
                placeholder={placeholder}
                multi
                isClearable
                isSearchable
                backspaceRemovesValue
                className={className}
                inputValue={this._inputValue}
                onInputChange={this._onInputChange}
            />
        );
    }

    @action.bound
    private _onSelectPermission(values: string | null) {
        this._selectedIds = values || '';
        this.props.onSelect?.(values);
    }

    @action.bound
    private _onInputChange(values: string) {
        this._inputValue = values;

        if (!this.props.enableStrDecoding) return;

        const decodedBytes = permissionStore.decodeBase64ToBytes(values);
        if (decodedBytes) {
            const permissionsIds = permissionStore.convertBytesToPermissionIds(decodedBytes);
            this._onSelectPermission(permissionsIds.join(','));
            this._inputValue = '';
        }
    }

    @computed
    private get _permissions(): PermissionModel[] {
        const permissions = permissionStore.permissions;
        return this.props.filterOptions ? this.props.filterOptions(permissions) : permissions;
    }

    @computed
    private get _permissionOptions() {
        const selectedIds = this._selectedIds.split(',').map(idStr => Number(idStr));

        const selectedOptions = [];
        const availableOptions = [];
        for (let p of this._permissions) {
            const option = { value: String(p.id), label: p.codeName || '' };
            if (selectedIds.includes(p.id)) {
                selectedOptions.push(option);
            } else {
                availableOptions.push(option);
            }
        }

        if (selectedOptions.length) {
            const headerSelected = {
                value: '-1',
                label: 'SELECTED'
            };
            const headerAvailable = {
                value: '-1',
                label: 'AVAILABLE'
            };

            selectedOptions.unshift(headerSelected);
            selectedOptions.push(headerAvailable);
        }

        return [...selectedOptions, ...availableOptions];
    }

    @computed
    public get permissions() {
        return this._permissions;
    }
}
